import {fromJust} from '@freckle/maybe'
import {formatRoundedNumber} from '@freckle/educator-entities/ts/common/helpers/common-table-helper'
import {toFragment} from '@freckle/educator-entities/ts/common/helpers/routers/query-params'
import type {RlDomainIdT} from '@freckle/educator-entities/ts/common/types/rl-domain-id'
import type {BaseTeacherAttrs} from '@freckle/educator-entities/ts/users/models/teacher'
import {formattedName} from '@freckle/educator-entities/ts/users/models/teacher'
import {Link} from '@freckle/educator-materials/ts/components/link'
import {Table} from '@freckle/educator-materials/ts/components/table/table'
import {useSafeTranslation} from '@freckle/educator-materials/ts/helpers/translate'
import {cellFromRow, aligned} from '@freckle/educator-materials/ts/logic/table/table'
import {Routes} from '@freckle/school/ts/common/routers/routes'
import {formatProgressBar} from '@freckle/school/ts/proficiency-report/components/school/table/utils'
import type {ProficiencyReportTableRowT} from '@freckle/school/ts/proficiency-report/logic/proficiency-report-table-logic'
import type {ProficiencyReportTeacherGroupDataT} from '@freckle/school/ts/proficiency-report/models/proficiency-report'

type Props = {
  tableRow: ProficiencyReportTableRowT
  teachers: Array<BaseTeacherAttrs>
  selectedGrade: number
  selectedDomainId: RlDomainIdT
}

type Row = ProficiencyReportTeacherGroupDataT

export default function ProficiencyReportTeacherTable({tableRow, teachers, ...props}: Props) {
  const {t} = useSafeTranslation()
  return (
    <Table
      data={tableRow.teacherGroups}
      columns={[
        {
          Header: t('PROFICIENCY_REPORT_TEACHER_TABLE_COLUMN_HEADER_TEACHER'),
          Cell: cellFromRow(({teacherId}: Row) => (
            <TeacherName teacherId={teacherId} teachers={teachers} />
          )),
          accessor: (d: Row) => d.teacherId,
          id: 'id',
          ...aligned('left')
        },
        {
          Header: t('PROFICIENCY_REPORT_TEACHER_TABLE_COLUMN_HEADER_GRADE_LEVEL'),
          Cell: cellFromRow(({averageGradeLevel}: Row) => (
            <>{formatRoundedNumber(2)(averageGradeLevel)}</>
          )),
          accessor: (d: Row) => d.averageGradeLevel,
          id: 'avg',
          ...aligned('left')
        },
        {
          Header: t('PROFICIENCY_REPORT_TEACHER_TABLE_COLUMN_HEADER_BELOW'),
          accessor: (d: Row) => d.belowGradeLevel,
          id: 'below',
          ...aligned('left')
        },
        {
          Header: t('PROFICIENCY_REPORT_TEACHER_TABLE_COLUMN_HEADER_WITHIN'),
          accessor: (d: Row) => d.withinGradeLevel,
          id: 'within',
          ...aligned('left')
        },
        {
          Header: t('PROFICIENCY_REPORT_TEACHER_TABLE_COLUMN_HEADER_ABOVE'),
          accessor: (d: Row) => d.aboveGradeLevel,
          id: 'above',
          ...aligned('left')
        },
        {
          Cell: cellFromRow((row: Row) => <TeacherProgressBar row={row} />),
          accessor: (d: Row) => d.averageGradeLevel,
          id: 'progress',
          ...aligned('center')
        },
        {
          Cell: cellFromRow((row: Row) => (
            <TeacherReportLink {...props} tableRow={tableRow} row={row} />
          )),
          accessor: (d: Row) => d.averageGradeLevel,
          id: 'cta',
          ...aligned('right')
        }
      ].map(c => ({...c, tdClassName: () => 'teacher-table-container'}))}
    />
  )
}

const TeacherName = ({
  teacherId,
  teachers
}: {
  teacherId: number
  teachers: Array<BaseTeacherAttrs>
}) => {
  const mTeacherModel = teachers.find(t => t.id === teacherId)
  const teacherModel = fromJust(mTeacherModel, `Could not find teacher: ${teacherId}`)

  return <>{formattedName(teacherModel)}</>
}

const TeacherProgressBar = ({row}: {row: Row}) =>
  formatProgressBar(row.belowGradeLevel, row.withinGradeLevel, row.aboveGradeLevel, x => x)

const TeacherReportLink = ({
  selectedGrade,
  selectedDomainId,
  row,
  tableRow: {schoolId}
}: {
  selectedGrade: number
  selectedDomainId: RlDomainIdT
  row: Row
  tableRow: ProficiencyReportTableRowT
}) => {
  const params = {grade: selectedGrade, domainId: selectedDomainId, schoolsIds: [schoolId]}

  const teacherReportRoute = toFragment(Routes.teacherProficiencyReport(row.teacherId), params)

  return (
    <Link href={teacherReportRoute} className="report-detail-link" reactRouter>
      <i className="fa fa-arrow-circle-o-right" />
    </Link>
  )
}
